import axios from "axios";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/userSlice";
import { selectAuth } from "../redux/authSlice";
import { selectSimulatedDate } from "../redux/simulatedDateSlice";

export function useRequest() {
  const user = useSelector(selectUser)
  const auth = useSelector(selectAuth)
  const simulatedDate = useSelector(selectSimulatedDate)

  let headers = {}
  let endpoint = ''
  let spoof = ''
  let currentDateParam = ''

  const setEndpoint = (end_pt) => {
    endpoint = end_pt
  }

  if (auth.accessToken) {
    headers = {
      Authorization: `Bearer ${auth.accessToken}`,
      'X-Requested-With': 'XMLHttpRequest',
    }
  }

  if (user.spoofed_user && user.spoofed_user.id) {
    spoof = `?spoofUserID=${user.spoofed_user.id}`
  }

  if (process.env.REACT_APP_ENVIRONMENT !== 'production' && simulatedDate?.simulated_date) {
    currentDateParam = `current_date=${encodeURIComponent(simulatedDate.simulated_date)}`
  }

  const appendQueryParam = (url, param) => {
    if (!param) return url
    const paramName = param.split('=')[0]
    if (url.includes(`${paramName}=`)) return url
    return url.includes('?') ? `${url}&${param}` : `${url}?${param}`
  }

  const makeGet = () => {
    if (endpoint?.includes('?')) {
      spoof = spoof.replace('?', '&')
    }
    endpoint += spoof
    endpoint = appendQueryParam(endpoint, currentDateParam)

    return axios.get(
      endpoint,
      {
        headers: headers
      }
    ).catch((e) => {
      // Get maintenance mode if we get a 503 error.
      if (e?.response?.status == 503) {
        window.location.href = '/maintenance'
      } else {
        throw e;
      }
    })
  }

  const makePost = async (data, file = null) => {
    if (file) {
      await addFileToPayload(data, file)
    }
    headers['Content-Type'] = 'application/json'
    headers['Accept'] = 'application/json'
    const postEndpoint = appendQueryParam(endpoint, data?.current_date ? '' : currentDateParam)
    return axios.post(
      postEndpoint,
      JSON.stringify(data),
      {
        headers: headers
      }
    );
  }

  // @TODO: Tech Debt: Why do we have an endpoint manually called here? This doesn't appear to be used.
  const makeFetchPost = (data, keepalive = false) => {
    headers['Content-Type'] = 'application/json'
    headers['Accept'] = 'application/json'
    headers['Authorization'] = `Bearer ${auth.accessToken}`
    headers['X-Requested-With'] = `XMLHttpRequest`
    fetch(`${process.env.REACT_APP_API_DOMAIN}/user_page_view`, {
      keepalive: keepalive,
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
    });
  }

  const makeEdit = async (data) => {
    headers['Content-Type'] = 'application/json'
    headers['Accept'] = 'application/json'
    const putEndpoint = appendQueryParam(endpoint, data?.current_date ? '' : currentDateParam)
    return axios.put(
      putEndpoint,
      JSON.stringify(data),
      {
        headers: headers
      }
    )
  }

  const readFile = async (data, file, fileType, index) => {
    if (file && (file instanceof File || file instanceof Blob)) {
      const fileExtension = file.name.split('.').pop();
      const fileExtensionProp = 'fileExtension' + (typeof index !== 'undefined' ? index : '');
      const fileNameProp = 'fileName' + (typeof index !== 'undefined' ? index : '');

      data[fileExtensionProp] = fileExtension;
      data[fileNameProp] = file.name;

      if (typeof fileType !== 'undefined' && typeof index !== 'undefined') {
        const fileTypeProp = 'fileType' + index;
        data[fileTypeProp] = fileType;
      }

      const fileProp = 'file' + (typeof index !== 'undefined' ? index : '');

      const reader = new FileReader();
      const promise = new Promise((resolve, reject) => {
        reader.onload = () => {
          // Update data with base64 encoded string of file
          data[fileProp] = reader.result.split(',')[1]; // Splitting to remove the MIME type prefix
          resolve();
        };
        reader.onerror = () => {
          reject(new Error('File reading failed'));
        };
      });
      reader.readAsDataURL(file);
      await promise; // Wait for file reading to complete
    }
  }

  const addFileToPayload = async (data, file) => {
    if (Array.isArray(file)) {
      const numberOfItems = file.length;
      if (numberOfItems === 1) {
        await readFile(data, file[0].file, file[0].type, 1)
      } else {
        const promises = [];
        for (let i = 1; i <= file.length; i++) {
          // eslint-disable-next-line no-await-in-loop
          const result = await (async () => {
            return readFile(data, file[(i - 1)].file, file[(i - 1)].type, i)
          })();
          promises.push(result);
        }
        await Promise.all(promises);
      }
    } else {
      await readFile(data, file)
    }
  }

  const readFileAsArray = async (file, fileType) => {
    if (file && (file instanceof File || file instanceof Blob)) {
      const fileExtension = file.name.split('.').pop();

      const reader = new FileReader();
      const promise = new Promise((resolve, reject) => {
        reader.onload = () => {
          // Return object with file data
          resolve({
            file: reader.result.split(',')[1], // Base64 string without MIME prefix
            fileExtension: fileExtension,
            fileName: file.name,
            fileType: fileType
          });
        };
        reader.onerror = () => {
          reject(new Error('File reading failed'));
        };
      });
      reader.readAsDataURL(file);
      return promise;
    }
    return null;
  };

  const addFileAsArrayToPayload = async (data, file) => {
    if (Array.isArray(file)) {
      // Process multiple files
      const filePromises = file.map((fileItem) => readFileAsArray(fileItem.file, fileItem.type));

      const fileResults = await Promise.all(filePromises);
      // Filter out any null results (in case some files failed to process)
      data.files = fileResults.filter((result) => result !== null);
    } else if (file) {
      // Process single file
      const fileResult = await readFile(file);
      if (fileResult) {
        data.files = [fileResult];
      }
    }
  };

  const makePartialEdit = async (data, file, fileAsArray = false) => {
    if (fileAsArray) {
      await addFileAsArrayToPayload(data, file)
    } else {
      await addFileToPayload(data, file)
    }

    headers['Content-Type'] = 'application/json'
    headers['Accept'] = 'application/json'
    const patchEndpoint = appendQueryParam(endpoint, data?.current_date ? '' : currentDateParam)
    return axios.patch(
      patchEndpoint,
      JSON.stringify(data),
      {
        headers: headers
      }
    )
  }

  const makeDelete = () => {
    const deleteEndpoint = appendQueryParam(endpoint, currentDateParam)
    return axios.delete(
      deleteEndpoint,
      {
        headers: headers
      }
    );
  }

  return {
    get: makeGet,
    post: makePost,
    fetchPost: makeFetchPost,
    edit: makeEdit,
    modify: makePartialEdit,
    delete: makeDelete,
    setEndpoint: setEndpoint,
  }
}
